import { useCallback, useMemo } from "react";
import { useAuthStore } from "../stores/auth.store";
import { useOrganizationStore } from "../stores/organization.store";
import { OrganizationType } from "../types/organization.type";
import {
  canAccessOrg,
  getSitesForOrg,
  resolveOpsNavPermission,
} from "../auth";
import type { OpsNavItem, OpsNavSubItem } from "../auth";
import { isReadOnlyAdmin } from "../utils/auth.helpers";

export function useAuthPermissions() {
  const {
    tokenPayload,
    permissionList,
    isPlatformAdmin: platformAdmin,
    user,
    canAccessPermission,
  } = useAuthStore();
  const { selectedOrganization } = useOrganizationStore();

  const orgId = selectedOrganization?.id ?? null;
  const readOnly = useMemo(
    () => isReadOnlyAdmin(user, tokenPayload),
    [user, tokenPayload],
  );

  const can = useCallback(
    (required: string, legacyRoles?: readonly string[]) =>
      canAccessPermission(required, legacyRoles, orgId),
    [canAccessPermission, orgId],
  );

  const canMutateAction = useCallback(
    (required: string, legacyRoles?: readonly string[]) =>
      !readOnly && can(required, legacyRoles),
    [readOnly, can],
  );

  const canSeeOpsNavItem = useCallback(
    (item: OpsNavItem | OpsNavSubItem): boolean => {
      if (platformAdmin) {
        return true;
      }

      const perm = resolveOpsNavPermission(item.entity, item.action, orgId);
      if (!perm) {
        return false;
      }
      return can(perm, item.legacyRoles);
    },
    [platformAdmin, orgId, can],
  );

  const filterOpsNavItems = useCallback(
    (items: OpsNavItem[], orgType: OrganizationType): OpsNavItem[] => {
      return items
        .filter((item) => item.allowedTypes.includes(orgType))
        .map((item) => {
          if (!canSeeOpsNavItem(item)) {
            return null;
          }
          if (item.subItems) {
            const visibleSubItems = item.subItems.filter(
              (subItem) =>
                (!subItem.allowedTypes || subItem.allowedTypes.includes(orgType)) &&
                canSeeOpsNavItem(subItem),
            );
            if (visibleSubItems.length === 0) {
              return null;
            }
            return { ...item, subItems: visibleSubItems };
          }
          return item;
        })
        .filter((e): e is OpsNavItem => e !== null);
    },
    [canSeeOpsNavItem],
  );

  const canAccessSelectedOrg = useCallback(
    () => canAccessOrg(tokenPayload, permissionList, orgId),
    [tokenPayload, permissionList, orgId],
  );

  const sitesForOrg = useCallback(
    (targetOrgId: string) => getSitesForOrg(tokenPayload, targetOrgId),
    [tokenPayload],
  );

  return {
    orgId,
    user,
    tokenPayload,
    permissionList,
    isPlatformAdmin: platformAdmin,
    isReadOnly: readOnly,
    can,
    canMutate: canMutateAction,
    canSeeOpsNavItem,
    filterOpsNavItems,
    canAccessSelectedOrg,
    sitesForOrg,
  };
}
